import axios, { AxiosError } from 'axios';
import { convertAxiosError, getAxiosHeaders } from './BackendInterface';
import { dataResidencyManager } from './DataResidencyManager';
import { IdpType, UserProfileType, UserType } from '../peekapak-types/DataProtocolTypes';
import { ElementaryUnitName, LessonLanguage } from '../peekapak-types/LessonPlanTypes';
import {
  EventName,
  LoginEvent,
  LogEventRequest,
  AnalyticsEvent,
  StudentEvent,
  EventSource,
  NonStudentEvent,
  InteractionEndTrigger,
  JournalAssignmentOpenEvent,
  JournalAssignmentStoryOpenEvent,
  LessonsStoryOpenEvent,
  LessonsOpenedEvent,
  StorybookOpenEvent,
  StorybookCloseEvent,
  StorybookCompleteEvent,
  InteractionStartEvent,
  InteractionEndEvent,
} from '../peekapak-types/AnalyticsTypes';
import { getAuthorizationToken } from './BackendInterface';
import { debounce } from './GlobalFunctions';

// Dynamic endpoint management based on data residency
function getAnalyticsServicesRoot(): string {
  const subdomain = dataResidencyManager.getApiSubdomain(); // Returns 'api' or 'api-uae'

  if (import.meta.env.REACT_TEST_PROD_API) {
    return `https://${subdomain}.peekapak.com/analyticsservices`;
  } else if (import.meta.env.MODE === 'development' || import.meta.env.REACT_USE_DEV_API) {
    return `https://${subdomain}.peekapak.com/analyticsservicesdev`;
  } else {
    return `https://${subdomain}.peekapak.com/analyticsservices`;
  }
}

const logEvent: (event: AnalyticsEvent) => Promise<void> = async (event: AnalyticsEvent) => {
  const auth = await getAuthorizationToken();
  const endpoint = `${getAnalyticsServicesRoot()}/analytics/log`;
  const request: LogEventRequest = {
    event,
  };
  try {
    await axios({
      method: 'post',
      url: endpoint,
      data: request,
      headers: getAxiosHeaders(auth),
    });
  } catch (error) {
    console.error(`Error communicating with server: ${convertAxiosError(error as AxiosError)}`);
    throw convertAxiosError(error as AxiosError);
  }
};

//utilize fetch to allow for keepalive flag, not currently supported by axios
const logEventWithFetchKeepalive: (event: AnalyticsEvent) => Promise<void> = async (event: AnalyticsEvent) => {
  const auth = await getAuthorizationToken();
  const endpoint = `${getAnalyticsServicesRoot()}/analytics/log`;
  const request: LogEventRequest = {
    event,
  };
  try {
    await fetch(endpoint, {
      keepalive: true,
      method: 'post',
      body: JSON.stringify(request),
      headers: {
        Authorization: auth,
        'X-User-Region': dataResidencyManager.getCurrentDataResidency(),
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    console.error(`Error communicating with server: ${convertAxiosError(error as AxiosError)}`);
    throw convertAxiosError(error as AxiosError);
  }
};

export const Analytics = {
  interactionStart: debounce(
    async (userProfile: UserProfileType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;

      const propertiesToLog = (() => {
        if (userType === UserType.STUDENT) {
          const { grade, readingLevel, classroomId } = userProfile;
          const interactionStartProperties: InteractionStartEvent = {
            name: EventName.INTERACTION_START,
            source: EventSource.PEEKAPAK,
            grade,
            readingLevel,
            classroomId,
            ...commonProperties,
            userType,
          };

          const properties = {
            ...interactionStartProperties,
            distinct_id: userId,
          };

          return properties;
        } else {
          const interactionStartProperties: InteractionStartEvent = {
            name: EventName.INTERACTION_START,
            source: EventSource.PEEKAPAK,
            ...commonProperties,
            userType,
          };

          const properties = {
            ...interactionStartProperties,
            distinct_id: userId,
          };

          return properties;
        }
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  interactionEnd: debounce(
    async (userProfile: UserProfileType, duration: number, trigger: InteractionEndTrigger) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;

      const propertiesToLog = (() => {
        if (userType === UserType.STUDENT) {
          const { grade, readingLevel, classroomId } = userProfile;
          const interactionEndProperties: InteractionEndEvent = {
            name: EventName.INTERACTION_END,
            source: EventSource.PEEKAPAK,
            grade,
            readingLevel,
            classroomId,
            ...commonProperties,
            userType,
            duration,
            trigger,
          };

          const properties = {
            ...interactionEndProperties,
            distinct_id: userId,
          };

          return properties;
        } else {
          const interactionEndProperties: InteractionEndEvent = {
            name: EventName.INTERACTION_END,
            source: EventSource.PEEKAPAK,
            ...commonProperties,
            userType,
            duration,
            trigger,
          };

          const properties = {
            ...interactionEndProperties,
            distinct_id: userId,
          };

          return properties;
        }
      })();

      await logEventWithFetchKeepalive(propertiesToLog);
    },
    500,
    true,
  ),
  journalAssignmentOpen: debounce(
    async (assignmentName: string, createdAt: number, userProfile: UserProfileType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType !== UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const { grade, readingLevel, classroomId } = userProfile;
        const assignmentOpenProperties: JournalAssignmentOpenEvent = {
          name: EventName.JOURNAL_ASSIGNMENT_OPEN,
          source: EventSource.PEEKAPAK,
          grade,
          readingLevel,
          classroomId,
          ...commonProperties,
          userType,
          assignmentName,
          createdAt,
        };

        const properties = {
          ...assignmentOpenProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  journalAssignmentStoryOpen: debounce(
    async (story: string, userProfile: UserProfileType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType !== UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const { grade, readingLevel, classroomId } = userProfile;
        const assignmentStoryOpenProperties: JournalAssignmentStoryOpenEvent = {
          name: EventName.JOURNAL_STORY_OPEN,
          source: EventSource.PEEKAPAK,
          grade,
          readingLevel,
          classroomId,
          ...commonProperties,
          userType,
          story,
        };

        const properties = {
          ...assignmentStoryOpenProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  lessonsStoryOpen: debounce(
    async (story: string, userProfile: UserProfileType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType === UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const lessonsStoryOpenProperties: LessonsStoryOpenEvent = {
          name: EventName.LESSONS_STORY_OPEN,
          source: EventSource.PEEKAPAK,
          ...commonProperties,
          userType,
          story,
        };

        const properties = {
          ...lessonsStoryOpenProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      const debouncedLogEvent = debounce(logEvent, 500, true);
      await debouncedLogEvent(propertiesToLog);
    },
    500,
    true,
  ),
  lessonsOpenedEvent: debounce(
    async (lesson: string, userProfile: UserProfileType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType === UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const lessonsOpenedProperties: LessonsOpenedEvent = {
          name: EventName.LESSONS_OPEN,
          source: EventSource.PEEKAPAK,
          ...commonProperties,
          userType,
          lesson,
        };

        const properties = {
          ...lessonsOpenedProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  login: debounce(
    async (userProfile: UserProfileType, method?: IdpType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;

      const propertiesToLog = (() => {
        if (userType === UserType.STUDENT) {
          const { grade, readingLevel, classroomId } = userProfile;
          const loginProperties: LoginEvent<StudentEvent> = {
            name: EventName.LOGIN,
            source: EventSource.PEEKAPAK,
            grade,
            readingLevel,
            classroomId,
            ...commonProperties,
            userType,
            loginMethod: method || 'unknown',
          };

          const properties = {
            ...loginProperties,
            distinct_id: userId,
          };

          return properties;
        } else {
          const loginProperties: LoginEvent<NonStudentEvent> = {
            name: EventName.LOGIN,
            source: EventSource.PEEKAPAK,
            ...commonProperties,
            userType,
            loginMethod: method || 'unknown',
          };

          const properties = {
            ...loginProperties,
            distinct_id: userId,
          };

          return properties;
        }
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  pageView: debounce(
    async (url: string, pageName: string | null = null, userProfile: UserProfileType | null = null) => {
      const userProperties = (() => {
        if (userProfile) {
          const commonProperties = extractCommonUserProperties(userProfile);
          const { userType, userId } = commonProperties;

          const propertiesToLog = (() => {
            if (userType === UserType.STUDENT) {
              const { grade, readingLevel, classroomId } = userProfile;
              const loginProperties: LoginEvent<StudentEvent> = {
                name: EventName.LOGIN,
                source: EventSource.PEEKAPAK,
                grade,
                readingLevel,
                classroomId,
                ...commonProperties,
                userType,
                loginMethod: 'unknown',
              };

              const properties = {
                ...loginProperties,
                distinct_id: userId,
              };

              return properties;
            } else {
              const loginProperties: LoginEvent<NonStudentEvent> = {
                name: EventName.LOGIN,
                source: EventSource.PEEKAPAK,
                ...commonProperties,
                userType,
                loginMethod: 'unknown',
              };

              const properties = {
                ...loginProperties,
                distinct_id: userId,
              };

              return properties;
            }
          })();
          return propertiesToLog;
        }

        return {};
      })();

      const propertiesToLog = {
        ...userProperties,
        name: EventName.PAGE_VIEW,
        source: EventSource.PEEKAPAK,
        url,
        pageName: pageName || 'none',
      };

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  storybookCloseEvent: debounce(
    async (
      unit: ElementaryUnitName,
      readingLevel: number,
      language: LessonLanguage,
      userProfile: UserProfileType,
      duration: number,
    ) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType === UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const storybookCloseProperties: StorybookCloseEvent<NonStudentEvent> = {
          name: EventName.JOURNAL_BOOK_CLOSE,
          source: EventSource.PEEKAPAK,
          ...commonProperties,
          unit,
          readingLevel,
          language,
          userType,
          duration,
        };

        const properties = {
          ...storybookCloseProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEventWithFetchKeepalive(propertiesToLog);
    },
    500,
    true,
  ),
  storybookCompleteEvent: debounce(
    async (unit: ElementaryUnitName, readingLevel: number, language: LessonLanguage, userProfile: UserProfileType) => {
      const commonProperties = extractCommonUserProperties(userProfile);
      const { userType, userId } = commonProperties;
      if (userType === UserType.STUDENT) return;

      const propertiesToLog = (() => {
        const storybookCompleteProperties: StorybookCompleteEvent<NonStudentEvent> = {
          name: EventName.JOURNAL_BOOK_COMPLETE,
          source: EventSource.PEEKAPAK,
          ...commonProperties,
          unit,
          readingLevel,
          language,
          userType,
        };

        const properties = {
          ...storybookCompleteProperties,
          distinct_id: userId,
        };

        return properties;
      })();

      await logEvent(propertiesToLog);
    },
    500,
    true,
  ),
  storybookOpenEvent: async (
    unit: ElementaryUnitName,
    readingLevel: number,
    language: LessonLanguage,
    userProfile: UserProfileType,
  ) => {
    const commonProperties = extractCommonUserProperties(userProfile);
    const { userType, userId } = commonProperties;
    if (userType === UserType.STUDENT) return;

    const propertiesToLog = (() => {
      const storybookOpenProperties: StorybookOpenEvent<NonStudentEvent> = {
        name: EventName.JOURNAL_BOOK_OPEN,
        source: EventSource.PEEKAPAK,
        ...commonProperties,
        unit,
        readingLevel,
        language,
        userType,
      };

      const properties = {
        ...storybookOpenProperties,
        distinct_id: userId,
      };

      return properties;
    })();

    await logEvent(propertiesToLog);
  },
};

function extractCommonUserProperties(userProfile: UserProfileType) {
  const { type, userId, schoolId, district, account, seller, licenseLevel, licenseExpires } = userProfile;

  return {
    userType: type as UserType,
    userId,
    schoolId: schoolId || 'none',
    district: district || 'none',
    account: account || 'none',
    seller: seller || 'none',
    licenseLevel,
    licenseExpires,
  };
}

export default Analytics;
